import { Component, EventEmitter, Inject, OnDestroy, ViewChild } from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';
import { DocumentUploadService } from '../../providers/document-upload.service';
import {
  MAT_DIALOG_DATA,
  MatDialogActions, MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { Organization } from "../../../common/models/organization";
import { Athlete } from "../../../common/models/athlete";
import { TranslateModule } from "@ngx-translate/core";
import { FlexLayoutModule } from "@angular/flex-layout";
import { MatList, MatListModule } from "@angular/material/list";
import { MatIcon } from "@angular/material/icon";
import { AsyncPipe, KeyValuePipe, NgForOf, NgIf } from "@angular/common";
import { MatProgressBar } from "@angular/material/progress-bar";
import { MatLine } from "@angular/material/core";
import { MatButton } from "@angular/material/button";

@Component({
  selector: 'app-document-dialog',
  templateUrl: './dialog.component.html',
  standalone: true,
  imports: [
    TranslateModule,
    FlexLayoutModule,
    MatDialogTitle,
    MatDialogContent,
    MatListModule,
    MatIcon,
    NgIf,
    MatProgressBar,
    MatLine,
    AsyncPipe,
    KeyValuePipe,
    NgForOf,
    MatDialogActions,
    MatDialogClose,
    MatButton
  ],
  styleUrls: ['./dialog.component.scss']
})
export class UploadDialogComponent implements OnDestroy {
    @ViewChild('file', {static: false}) public file: any;

    public files: Set<File> = new Set();

    public progress: any;
    public canBeClosed = true;
    public primaryButtonText = 'DocumentUpload';
    public showCancelButton = true;
    public uploading = false;
    public uploadSuccessful = false;

    public filesUploaded: EventEmitter<any> = new EventEmitter();

    private organization: Organization = {} as Organization;
    private athlete: Athlete = {} as Athlete;
    private subscriptions: Subscription[] = [];

    constructor(
        public dialogRef: MatDialogRef<UploadDialogComponent>,
        public uploadService: DocumentUploadService,
        @Inject(MAT_DIALOG_DATA) public data: any,
    ) {
        this.organization = this.data.organization;
        this.athlete = this.data.athlete;
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    public onFilesAdded() {
        const files: { [key: string]: File } = this.file.nativeElement.files;
        for (const key in files) {
            if (!isNaN(parseInt(key, 10))) {
                this.files.add(files[key]);
            }
        }
    }

    public addFiles() {
        this.file.nativeElement.click();
    }

    public hasFiles(): boolean {
        return this.files.size > 0;
    }

    public closeDialog() {
        // if everything was uploaded already, just close the dialog
        if (this.uploadSuccessful) {
            return this.dialogRef.close();
        }

        this.uploading = true;

        // start the upload and save the progress map
        this.progress = this.uploadService.upload(this.organization, this.athlete, this.files);
        for (const key in this.progress) {
            this.subscriptions.push(
                this.progress[key].observable.subscribe()
            );
        }

        // convert the progress map into an array
        const allProgressObservables = [];
        for (const key in this.progress) {
            allProgressObservables.push(this.progress[key].observable);
        }

        // The OK-button should have the text "Finish" now
        this.primaryButtonText = 'Close';

        // The dialog should not be closed while uploading
        this.canBeClosed = false;
        this.dialogRef.disableClose = true;

        // Hide the cancel-button
        this.showCancelButton = false;

        // When all progress-observables are completed...
        this.subscriptions.push(
            forkJoin(allProgressObservables).subscribe((end) => {
                this.canBeClosed = true;
                this.dialogRef.disableClose = false;
                this.uploadSuccessful = true;
                this.uploading = false;

                this.filesUploaded.emit();
            })
        );
    }
}
